import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { readAuthConfig } from '../auth/authConfig'
import { probeEmailAuthoritative } from '../auth/emailProbe'
import {
  completeNewPassword,
  confirmForgotPassword,
  confirmSignUp,
  forgotPassword,
  resendCode,
  signIn,
  signUp,
  AuthFlowError,
  type NewPasswordPending,
} from '../auth/cognitoAuth'
import { normalizeEmail } from '../auth/normalizeEmail'
import {
  CODE_INPUT_MAX_LENGTH,
  CODE_INPUT_MIN_LENGTH,
  clearPendingEmailCode,
  confirmClaimCode,
  freshPendingEmailCode,
  loadPendingEmailCode,
  parseSignInDeepLink,
  requestEmailCode,
  resendClaimCode,
  signInAfterClaim,
  startEmailClaim,
  storePendingEmailCode,
  submitEmailCode,
} from '../auth/userAuth'
import { RETURN_TO_PARAM, currentReturnPath, sanitizeReturnTo } from '../auth/sessionExpiry'
import { onReminderLandingEmail } from '../lib/reminderClick'
import { listenForHandedOffCode, offerCodeToWaitingTab, stripCodeFromUrl } from '../auth/codeHandoff'
import { storeSession } from '../auth/session'
import type { TokenSet } from '../auth/oidc'
import type { PendingEmailCode } from '../auth/userAuth'
import { markLegacyPrefillPending } from '../data/myApi'
import { loadLegacyDraft } from './wizard/profileStore'
import { Brand, Button } from '../components/ui'
import { EmailHiddenCharNotice, useCleanEmail } from '../components/EmailHiddenCharNotice'

/*
 * PARTICIPANT SIGN-IN (my.trumin), fully in-app (no hosted UI — the browser
 * NEVER navigates to *.amazoncognito.com). One component, one state machine.
 *
 * PRIMARY = EMAIL CODE (PROJ-0077-WU-04 / PROGRAMME-DESIGN-AUTH-POPULATION-MODEL
 * -V1 v3, Duncan-LOCKED + PRD-AUTH-POPULATION-IMPLEMENTATION-V1): the native
 * Cognito USER_AUTH choice-based flow (src/auth/userAuth.ts — InitiateAuth
 * AuthFlow=USER_AUTH → EMAIL_OTP → RespondToAuthChallenge). The EMAIL step's
 * primary action emails an 8-digit code (EMAIL_OTP_CODE_LENGTH); the CODE step enters it.
 * CLAIM-BY-EMAIL-CODE (BOARDROOM-2026-10-02-090708, PROJ-0077 claim module): an
 * address with NO account or an UNCONFIRMED one cannot take an EMAIL_OTP, so it
 * is claimed instead — SignUp (generated, never-shown password) → the 6-digit
 * sign-up code on the SAME, identical CODE step → ConfirmSignUp → sign-in after
 * sign-up (or one OTP fallback). See sendCode / submitClaimCode
 * (autocomplete="one-time-code", large code box per the approved 2026-09-16
 * branded template). The branded email's deep-link lands on the CODE step
 * PRE-FILLED and NEVER auto-submits (LOCKED canon behaviour) — an explicit user
 * action is always required. Emailed-code = control-of-mailbox is the DELIBERATE
 * low-privilege security floor (Duncan build-caveat (c)). WU-05 passkey
 * (WEB_AUTHN) is purely additive on the SAME USER_AUTH entry point later.
 *
 * SECONDARY (legacy/optional) = PASSWORD: the pre-existing email-first
 * USER_SRP_AUTH state machine below (cognitoAuth.ts) is DEMOTED to a "use a
 * password instead" affordance. It is retained intact — working session
 * plumbing (session.ts), the create/confirm/forgot branches, and the
 * order-confirmation ?email= creation deep-link are unchanged:
 *
 *   EMAIL → CHECKING → (no-account/exists/indet) SET_PASSWORD →
 *                          signUp ok        → CONFIRM_CODE → AUTHING → DONE
 *                          UsernameExists   → signIn(entered pw):
 *                                               ok    → DONE
 *                                               wrong → PASSWORD → DONE
 *                     (unconfirmed)  CONFIRM_CODE(resume) → PASSWORD
 *                     (reset-req.)   FORGOT_SEND → FORGOT_CODE → PASSWORD
 *   PASSWORD / SET_PASSWORD  "Forgot?" → FORGOT_SEND → FORGOT_CODE → PASSWORD
 *     (BOARDROOM-2026-09-01-180926: forgot is on BOTH password steps; a hard
 *      failure returns to the step it came from; EMAIL carries an explicit
 *      "New to Trumin? Create an account" → SET_PASSWORD, no probe)
 *   PASSWORD  newPasswordRequired → NEW_PASSWORD → DONE
 *   DONE = storeSession(tokens) then window.location.replace('/').
 *
 * IDENTIFIER-FIRST, dead-end fix (BOARDROOM-2026-08-14-095149): a NEW buyer
 * arriving from the order-confirmation "Go to My Trumin" link must land on
 * account CREATION, never on a sign-in "Incorrect email or password" dead-end.
 * The consumer pool has PreventUserExistenceErrors ENABLED, so the passwordless
 * SRP probe (emailProbe.ts) returns PASSWORD_VERIFIER ('exists') even for
 * brand-NEW emails — it CANNOT tell a new buyer from a returning one. So we no
 * longer let the probe gate a new email into sign-in: every ambiguous result
 * routes to the NEUTRAL SET_PASSWORD step, which CREATES the account for a new
 * email (self-signUp) and transparently signs in an existing one (the proven
 * ClaimLanding smart-path). A new email therefore never reaches a sign-in error.
 * The email can be prefilled via the deep-link (?email=…) and auto-checked.
 *
 * Auth calls go through src/auth/cognitoAuth.ts (USER_SRP_AUTH over
 * amazon-cognito-identity-js); session.ts stays the single session source and
 * the app's auth gate remains loadSession() !== null.
 *
 * AUTHORITATIVE DISCRIMINATOR (CMT-2026-08-28-006, BOARDROOM-2026-08-27-214424):
 * the my-api existence endpoint recommended above is now BUILT — POST
 * /account/probe {email} → {status} via AdminGetUser (grant already held).
 * probeEmailAuthoritative consults it first: 'confirmed' routes a returning
 * user straight to the PASSWORD ("Enter password") step; 'not-found' to
 * CREATE. When my-api is unreachable the SRP probe below remains the floor
 * (every ambiguous result still lands on the neutral SET_PASSWORD step), so
 * an api outage can never make login worse than the pre-fix behaviour.
 *
 * Legacy account marry-up (CMT-035, Approach B = JIT link-on-first-login): the
 * NEW-ACCOUNT branch (CREATE_PASSWORD → …→ auto sign-in) checks, once the
 * session exists, whether this verified email matches a legacy account. On a
 * match the profile wizard OPENS PRE-FILLED for review (finishNewAccount below);
 * no match ⇒ the normal home redirect, unchanged. The datalake LOOKUP is server-
 * side + currently pending, so today this always resolves "no match" — the
 * wiring is inert-but-ready. PII never enters a URL/query or storage (only a
 * boolean marker; the wizard re-fetches the profile via the authed endpoint).
 */

type Step =
  | 'EMAIL'
  | 'SENDING_CODE' // transient: requesting the email code (primary path)
  | 'CODE' // enter the emailed code — 8-digit OTP or 6-digit claim code (primary path)
  | 'CHECKING'
  | 'PASSWORD'
  | 'CREATE_PASSWORD'
  | 'CONFIRM_CODE'
  | 'AUTHING'
  | 'NEW_PASSWORD'
  | 'FORGOT_SEND'
  | 'FORGOT_CODE'

// Styles — inputs per ProfileWizard inputStyle (canon §5); frame per the
// existing Login (360 column, Brand 34, "My Trumin").
const inputStyle: CSSProperties = {
  width: '100%', minHeight: 46, border: '1px solid var(--hairline-14)',
  borderRadius: 11, padding: '0 13px', fontSize: 15, background: 'var(--bg-frame)',
  color: 'var(--text)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
}
const labelStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6,
}
const linkBtnStyle: CSSProperties = {
  border: 0, background: 'transparent', color: 'var(--accent-bright)', fontFamily: 'inherit',
  fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 0,
}

/** Mask an email for the confirm-code heading — first char + domain kept. */
function maskEmail(email: string): string {
  const at = email.indexOf('@')
  if (at <= 0) return email
  const local = email.slice(0, at)
  const domain = email.slice(at)
  const head = local[0]
  return `${head}${'•'.repeat(Math.max(1, local.length - 1))}${domain}`
}

const RESEND_COOLDOWN_S = 30

/** Code calls target the resolved Cognito username VERBATIM (see codeUsername). */
const EXACT = { exactUsername: true } as const

/** Forgot-password notice — HONEST under anti-enumeration (BOARDROOM-2026-09-
 *  11-115601, the dave@cab-sports.com silence): Cognito's
 *  PreventUserExistenceErrors makes a reset request on a NON-existent address
 *  "succeed" with no email ever arriving, so an unconditional "We emailed you
 *  a reset code" strands that user in silence. The copy states the truth the
 *  flow can actually promise — and names the no-code exit (the address has no
 *  account → create one) without confirming existence either way. */
/** CODE-step copy — IDENTICAL whichever code was sent (EMAIL_OTP for a verified
 *  account, sign-up verification code for a claim), so the screen never reveals
 *  whether the address had an account (BOARDROOM-2026-10-02-090708). Deliberately
 *  states no digit count: the OTP is 8 digits, the claim code 6. */
const CODE_SENT_NOTICE = 'We emailed you a sign-in code.'
/** Shown only AFTER the user proved mailbox control with a claim code, when
 *  Cognito's sign-in-after-sign-up did not complete and one EMAIL_OTP is needed. */
const CLAIM_FINISH_NOTICE = 'Your email is confirmed. We’ve sent one more code to finish signing in.'

const FORGOT_SENT_NOTICE =
  'If you have a Trumin account, we’ve emailed you a reset code. Not received it within a few minutes? '
  + 'Check spam, or tap “Email me a sign-in code” below: it works even if you’ve never signed in here, '
  + 'including past customers from our old system.'

/** Drop ?email= from the address bar (history.replaceState — no navigation),
 *  keeping every other param, so a reload never re-runs the auto-send. */
function stripEmailParam() {
  try {
    const params = new URLSearchParams(window.location.search)
    if (!params.has('email')) return
    params.delete('email')
    const q = params.toString()
    const path = window.location.pathname || '/'
    window.history.replaceState(window.history.state, '', `${path}${q ? `?${q}` : ''}${window.location.hash || ''}`)
  } catch {
    // no-op: a non-strippable URL only means a reload resumes via the fresh record
  }
}

export function Login() {
  // Email-first only needs pool/client/region — NOT a hosted-UI domain.
  const configured = useMemo(() => readAuthConfig() !== null, [])

  // The order-confirmation deep-link carries the buyer's known email
  // (?email=…). We prefill it and auto-run the existence check so a new buyer
  // is taken straight into account creation — "we know the email" (Duncan's
  // ruling, BOARDROOM-2026-08-14-095149).
  const prefillEmail = useMemo(() => {
    try {
      return (new URLSearchParams(window.location.search).get('email') ?? '').trim()
    } catch {
      return ''
    }
  }, [])

  // EMAIL-CODE deep-link (the branded sign-in email's button, PRIMARY path).
  // PINNED to the settings-side single-source contract (BOARDROOM-2026-09-19-
  // 211648): the link is EXACTLY /sign-in?code=<code>[&u=<hint>] — code ONLY, NO
  // email param. Parsed PURELY (parseSignInDeepLink returns data only) so we
  // PRE-FILL the code and NEVER auto-submit — the LOCKED canon behaviour
  // (PROGRAMME-DESIGN-AUTH-POPULATION-MODEL-V1 v3). Distinct from the
  // order-confirmation ?email= (no code) creation link handled below: this fires
  // ONLY when a code is present. Because the link carries no email, the email is
  // recovered from the same-browser pending-challenge record (see below).
  const codeDeepLink = useMemo(() => {
    try {
      return parseSignInDeepLink(window.location.search)
    } catch {
      return null
    }
  }, [])

  // RETURN PATH (CMT-2026-09-03-026): where sign-in lands afterwards.
  // Priority: an explicit ?returnTo=… (the session-expiry redirect carries the
  // path the user was on) → else the deep path Login itself rendered at (the
  // unauthed gate shows Login at ANY route, so a cold entry on /orders/x must
  // return to /orders/x, not '/'). Both validated same-origin; '/' ⇒ null.
  // The sign-in email's button (/sign-in?code=…) opens a NEW page with no path
  // of its own: it lands where THIS browser was when it asked for the code
  // (stored on the pending-challenge record), never on /sign-in itself.
  const returnTo = useMemo(() => {
    try {
      const explicit = sanitizeReturnTo(new URLSearchParams(window.location.search).get(RETURN_TO_PARAM))
      if (explicit) return explicit
      if (codeDeepLink) return sanitizeReturnTo(loadPendingEmailCode()?.returnTo)
      return window.location.pathname !== '/' ? sanitizeReturnTo(currentReturnPath()) : null
    } catch {
      return null
    }
  }, [])

  // A code deep-link boots STRAIGHT into the CODE step with the code PRE-FILLED
  // (never auto-submitted — see submit gating below).
  const [step, setStep] = useState<Step>(codeDeepLink ? 'CODE' : 'EMAIL')
  // A code deep-link carries NO email by contract, so recover it from the
  // same-browser pending-challenge record (persisted when THIS browser requested
  // the code). A cross-device open has none — the CODE step then honestly offers
  // "resend a fresh code" (see submitCode's no-session guard). Non-code entries
  // keep the order-confirmation ?email= prefill unchanged.
  const [email, setEmail, emailHidden] = useCleanEmail(() =>
    codeDeepLink ? (loadPendingEmailCode()?.email ?? prefillEmail) : prefillEmail,
  )
  const [password, setPassword] = useState('') // the field for the current step
  const [code, setCode] = useState(codeDeepLink?.code ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Cross-tab hand-off (Duncan GO 2026-10-22): this email-link tab gave its
  // code to the person's waiting tab — show "continue there".
  const [handedOff, setHandedOff] = useState(false)
  const [resend, setResend] = useState(0) // cooldown seconds remaining
  // RATE-LIMIT countdown (2026-10-08, Duncan: nobody locked out at an event):
  // a my-api 429 with Retry-After shows "try again in N seconds" counting down,
  // and the send buttons wait — instead of a dead-end error.
  const [rateWait, setRateWait] = useState(0)
  // Masked delivery destination Cognito returns for the code (e.g. "j***@e***").
  const [codeDestination, setCodeDestination] = useState<string | null>(null)

  // A password captured at CREATE_PASSWORD is held IN MEMORY so a successful
  // CONFIRM_CODE can auto-sign-in (never persisted, never logged).
  const createdPassword = useRef<string | null>(null)
  // Live CognitoUser challenge for the NEW_PASSWORD state.
  const pendingNewPw = useRef<NewPasswordPending | null>(null)
  // In-memory Cognito Session for the EMAIL_OTP challenge (the one that sent the
  // current code). Mirrors userAuth's localStorage pending record so a deep-link
  // opened in a fresh tab can still complete the same challenge.
  const emailCodeSession = useRef<string | null>(null)
  // Which code the CODE step is answering: an EMAIL_OTP ('otp') or a claim
  // sign-up verification code ('claim', BOARDROOM-2026-10-02-090708). Never
  // rendered — the CODE step looks identical either way (non-enumerating).
  const codeMode = useRef<'otp' | 'claim'>('otp')
  // The claim SignUp Session (branch a only) — echoed to ConfirmSignUp so
  // Cognito can sign the user in after sign-up with no second code.
  const claimSession = useRef<string | null>(null)
  // The EXACT Cognito username the current code was sent to — the probe-
  // RESOLVED, case-correct one (#275 release blocker: the pool is case-
  // sensitive and legacy usernames can be mixed-case). Every later call
  // (ConfirmSignUp, sign-in after sign-up, OTP fallback, resend, the OTP
  // answer) targets THIS, never a value re-derived from the typed email.
  const codeUsername = useRef<string | null>(null)
  /** Wire username for the code calls, used VERBATIM (exactUsername). */
  const codeUser = () => codeUsername.current ?? normalizeEmail(email)
  // Set when a claim was confirmed but Cognito's sign-in-after-sign-up did not
  // complete, so the OTP-fallback sign-in still lands via finishNewAccount (the
  // first-login legacy marry-up lookup) rather than the plain finish().
  const claimConfirmed = useRef(false)
  // One-shot guard so a prefilled deep-link email auto-advances only once.
  const autoAdvanced = useRef(false)
  /** Set just before the ?email= auto-send so armCode marks the record autoSent. */
  const autoSendRef = useRef(false)
  // The case-CORRECT Cognito username the probe resolved this email to
  // (Duncan checklist #3, BOARDROOM-2026-09-02-191616). The pool is case-
  // sensitive and legacy usernames were minted as-typed, so the FORGOT flow
  // must target this value — a lowercased mixed-case username makes
  // ForgotPassword a silent no-op (PreventUserExistenceErrors simulates
  // success). null (probe skipped/failed) falls back to the canonical
  // lowercase form. Display keeps whatever the user typed; only the wire
  // identity lives here.
  const resolvedUsername = useRef<string | null>(null)
  const authUsername = () => resolvedUsername.current ?? normalizeEmail(email)

  // Resend cooldown ticker.
  useEffect(() => {
    if (resend <= 0) return
    const t = setInterval(() => setResend((s) => (s <= 1 ? 0 : s - 1)), 1000)
    return () => clearInterval(t)
  }, [resend])

  const emailValid = /.+@.+\..+/.test(email.trim())

  // BROWSER AUTOFILL (quality 2026-10-22): a password-manager / browser
  // autofill shows the address in the field but does NOT fire onChange, so
  // `email` stayed '' and "Email me a sign-in code" stayed disabled until the
  // address was retyped. Pull the field's real value into state whenever it
  // differs: on any pointer/focus/key interaction in the step, and on a light
  // poll while the email step shows an empty state (the browser only exposes
  // the autofilled value after the first user interaction).
  const emailInputRef = useRef<HTMLInputElement | null>(null)
  const syncEmailFromField = () => {
    const v = emailInputRef.current?.value
    if (typeof v === 'string' && v !== '' && v !== email) setEmail(v)
  }
  useEffect(() => {
    if (step !== 'EMAIL' || email !== '') return
    const id = setInterval(() => {
      const v = emailInputRef.current?.value
      if (typeof v === 'string' && v !== '') setEmail(v)
    }, 300)
    return () => clearInterval(id)
  }, [step, email])

  // CROSS-TAB HAND-OFF (Duncan GO 2026-10-22; the email link never signs in
  // on its own). WAITING tab (asked for a code here, now on "Check your
  // email"): accept a code another my.trumin tab offers, fill the field —
  // the person still presses Sign in.
  useEffect(() => {
    if (step !== 'CODE' || codeDeepLink) return
    return listenForHandedOffCode((c) => {
      setCode(c)
      setNotice('Your code arrived from the email — press Sign in.')
    })
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  // EMAIL-LINK tab: drop the code from the URL, offer it to a waiting tab;
  // an ack within ~1 s ⇒ "continue in your other tab", else today's form
  // with the code filled in.
  useEffect(() => {
    if (!codeDeepLink) return
    stripCodeFromUrl()
    let live = true
    void offerCodeToWaitingTab(codeDeepLink.code).then((ok) => {
      if (live && ok) setHandedOff(true)
    })
    return () => {
      live = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // EMAIL-CODE deep-link boot: adopt the persisted Cognito challenge session (if
  // this SAME browser requested the code) so the pre-filled code can complete
  // the very challenge that emailed it — WITHOUT re-issuing (which would
  // invalidate the code the user holds). We do NOT submit here: prefill only,
  // explicit action required (LOCKED canon). A different-device link has no
  // persisted session → the CODE step offers "resend a fresh code".
  useEffect(() => {
    if (rateWait <= 0) return
    const t = setInterval(() => setRateWait((s) => (s <= 1 ? 0 : s - 1)), 1000)
    return () => clearInterval(t)
  }, [rateWait > 0]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!codeDeepLink) return
    // The link carries no email, so the persisted record IS the email+session
    // source (already seeded into `email` above). Adopt its challenge session so
    // the pre-filled code can complete the very challenge that emailed it.
    const pending = loadPendingEmailCode()
    codeUsername.current = pending?.username ?? null
    if (pending?.kind === 'claim') {
      codeMode.current = 'claim'
      claimSession.current = pending.session || null
    } else if (pending) {
      emailCodeSession.current = pending.session
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Order-confirmation ?email= (no code) auto-advance: a valid prefilled email
  // lands in the EMAIL-CODE lane — the sign-in code is sent at once and the
  // buyer goes straight to the CODE step, never a password screen (LOCKED
  // PROGRAMME-DESIGN-AUTH-POPULATION-MODEL-V1 v3: participants = email code
  // first, guest buyers claim in the email-code lane; boardroom conformance
  // fix 2026-10-08). requestCode serves every case with the same copy: a
  // verified account gets EMAIL_OTP, a guest/unverified buyer the claim code.
  // The ONE password fallback is requestCode's own 'password-only' outcome (a
  // confirmed account whose email was never verified: Cognito can email it no
  // code at all). Suppressed when a code deep-link is present (that lands on
  // CODE with the code it carries — re-sending would invalidate it).
  //
  // RELOAD / MAIL-SCANNER GUARD (boardroom 2026-10-08): each load used to send
  // a NEW code, so reloads multiplied emails (only the newest code works) and
  // could hit Cognito's send limit. Now: (b) ?email= is stripped from the URL
  // on the first auto-send, so a reload never re-triggers it; and (a) a code
  // THIS browser sent within its freshness window is RESUMED on the CODE step
  // with no new send — on a re-click of the link (same email) or a bare reload
  // (auto-sent record only). A stale record re-sends. "Resend code" on the
  // CODE step stays the only manual resend.
  useEffect(() => {
    if (autoAdvanced.current || codeDeepLink || !configured) return
    if (prefillEmail && !emailValid) return
    const resume = prefillEmail
      ? freshPendingEmailCode({ email: prefillEmail })
      : freshPendingEmailCode({ autoSentOnly: true })
    if (!prefillEmail && !resume) return
    autoAdvanced.current = true
    stripEmailParam()
    if (resume) {
      resumePendingCode(resume)
      return
    }
    autoSendRef.current = true
    void requestCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // REMINDER LANDING PRE-FILL (boardroom GO 2026-11-01; my-api
  // MY_REMINDER_LANDING_PREFILL_ENABLED): an assign reminder's click answers
  // the buyer's own address in the response body (src/lib/reminderClick.ts,
  // memory only — never the URL). It runs the SAME lane as the order-
  // confirmation ?email= above: pre-fill, then ONE code (or resume a fresh one
  // this browser already sent — the reload guard). Never when a ?email= or a
  // code link drives the screen, after a step has moved on, or over an
  // address the person has already typed.
  const [landingEmail, setLandingEmailState] = useState<string | null>(null)
  useEffect(() => {
    if (codeDeepLink || prefillEmail || !configured) return
    return onReminderLandingEmail((e) => setLandingEmailState(e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (!landingEmail || autoAdvanced.current || step !== 'EMAIL') return
    if (normalizeEmail(email) !== normalizeEmail(landingEmail)) {
      if (email.trim() === '') setEmail(landingEmail)
      return
    }
    if (!emailValid) return
    autoAdvanced.current = true
    const resume = freshPendingEmailCode({ email: landingEmail })
    if (resume) {
      resumePendingCode(resume)
      return
    }
    autoSendRef.current = true
    void requestCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingEmail, email])

  /** Land on the CODE step for a code this browser already sent — no new send. */
  function resumePendingCode(p: PendingEmailCode) {
    setEmail(p.email)
    codeUsername.current = p.username ?? null
    if (p.kind === 'claim') {
      codeMode.current = 'claim'
      claimSession.current = p.session || null
      emailCodeSession.current = null
    } else {
      codeMode.current = 'otp'
      emailCodeSession.current = p.session
      claimSession.current = null
    }
    setCodeDestination(null)
    resetTransientAt('CODE')
    setNotice(CODE_SENT_NOTICE)
  }

  function resetTransientAt(next: Step) {
    setError(null)
    setNotice(null)
    setPassword('')
    setCode('')
    setStep(next)
  }

  /** Show a code-SEND failure: a rate limit with a known wait becomes the
   *  live countdown; anything else the existing neutral send copy. */
  function showSendError(err: AuthFlowError) {
    if (err.code === 'TooManyRequests' && err.retryAfterSeconds) {
      setError(null)
      setRateWait(Math.min(err.retryAfterSeconds, 900))
      return
    }
    setError(sendErrorCopy(err))
  }

  function fail(e: unknown): AuthFlowError {
    const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', e instanceof Error ? e.message : 'Something went wrong.')
    setError(mapErrorCopy(err))
    return err
  }

  function finish(tokens: TokenSet) {
    createdPassword.current = null
    storeSession(tokens)
    // Land back where the session died / where the deep link pointed
    // (CMT-2026-09-03-026) — home stays the default.
    window.location.replace(returnTo ?? '/')
  }

  /**
   * Finish for the NEW-ACCOUNT branch (CMT-035 marry-up). Stores the session,
   * then checks for a legacy match: on a hit, mark the one-shot pre-fill marker
   * and open the profile wizard (pre-filled for review); otherwise the normal
   * home redirect. The legacy check is best-effort — any failure (or no api
   * base / pending datalake lookup) degrades silently to the home redirect and
   * NEVER blocks account creation.
   */
  async function finishNewAccount(tokens: TokenSet) {
    createdPassword.current = null
    storeSession(tokens)
    try {
      const legacy = await loadLegacyDraft()
      if (legacy) {
        markLegacyPrefillPending()
        window.location.replace('/get-ready/profile/step-1')
        return
      }
    } catch {
      // never block a brand-new sign-in on a legacy-lookup hiccup
    }
    // No legacy review ⇒ same return-path landing as finish() (the legacy
    // wizard review above deliberately outranks returnTo — it is the one-shot
    // account-marry-up moment and must not be skipped).
    window.location.replace(returnTo ?? '/')
  }

  // --- transitions ---------------------------------------------------------

  async function submitEmail() {
    if (!emailValid || busy) return
    setError(null)
    setNotice(null)
    setBusy(true)
    setStep('CHECKING')
    resolvedUsername.current = null
    try {
      const probe = await probeEmailAuthoritative(email.trim())
      resolvedUsername.current = probe.username
      setBusy(false)
      switch (probe.result) {
        case 'exists-confirmed':
          // AUTHORITATIVE existing-account signal (my-api /account/probe via
          // AdminGetUser — CMT-2026-08-28-006): a returning user goes straight
          // to "Enter password", never the create branch.
          resetTransientAt('PASSWORD')
          break
        case 'unconfirmed':
          createdPassword.current = null // resume path — no held password
          resetTransientAt('CONFIRM_CODE')
          setNotice('We emailed you a code to confirm this address.')
          break
        case 'reset-required':
          await beginForgot()
          break
        case 'no-account':
        case 'exists':
        case 'indeterminate':
        default:
          // 'exists' here is the AMBIGUOUS SRP-probe fallback (my-api probe
          // unreachable): PreventUserExistenceErrors makes it fire even for
          // brand-NEW emails, so we CANNOT trust it to send a new buyer into
          // sign-in (the launch dead-end). Route every ambiguous result to the
          // neutral SET_PASSWORD step, which creates the account for a new
          // email and signs an existing one straight in — a new email never
          // reaches an "Incorrect email or password" error.
          resetTransientAt('CREATE_PASSWORD')
          break
      }
    } catch {
      // Probe surfaced an error (e.g. SRP flow off) — go to SET_PASSWORD (which
      // handles both new and existing) rather than blocking or dead-ending.
      setBusy(false)
      resetTransientAt('CREATE_PASSWORD')
    }
  }

  // --- PRIMARY: native USER_AUTH email-code (WU-04) -------------------------

  /** The persisted challenge session iff it belongs to the CURRENT email. */
  function matchedPendingSession(): string | null {
    const pending = loadPendingEmailCode()
    return pending && pending.kind !== 'claim' && normalizeEmail(pending.email) === normalizeEmail(email)
      ? pending.session
      : null
  }

  /** Arm the CODE step's state for the code just sent (OTP or claim). The
   *  pending record carries the mode so a deep-link opened in a fresh tab
   *  answers the right Cognito call. Nothing mode-specific is rendered. */
  function armCode(
    mode: 'otp' | 'claim',
    session: string | undefined,
    destination: string | undefined,
    username: string | undefined,
  ) {
    codeMode.current = mode
    codeUsername.current = username ?? codeUsername.current
    if (mode === 'otp') {
      emailCodeSession.current = session ?? null
      claimSession.current = null
    } else {
      emailCodeSession.current = null
      claimSession.current = session ?? null
    }
    // sentAt + autoSent feed the ?email= reload guard (freshPendingEmailCode).
    // A resend for the SAME address keeps the auto-sent mark, so a bare reload
    // after "Resend code" still resumes on CODE instead of a blank email step.
    const prev = loadPendingEmailCode()
    const autoSent = autoSendRef.current
      || (prev?.autoSent === true && normalizeEmail(prev.email) === normalizeEmail(email.trim()))
    autoSendRef.current = false
    storePendingEmailCode({
      email: email.trim(),
      session: session ?? '',
      kind: mode,
      ...(codeUsername.current ? { username: codeUsername.current } : {}),
      sentAt: Date.now(),
      ...(autoSent ? { autoSent: true } : {}),
      ...(returnTo ? { returnTo } : {}),
    })
    setCodeDestination(destination ?? null)
  }

  /**
   * Send ONE code for the current email (BOARDROOM-2026-10-02-090708), via
   * startEmailClaim, which consults the AUTHORITATIVE my-api probe FIRST:
   *  (a) no account          → SignUp (lowercase) → sign-up verification code;
   *  (c) existing verified   → EMAIL_OTP to the probe-resolved username;
   *  (b) existing unconfirmed→ my-api PREPARE, then ONE EMAIL_OTP to that
   *      username (no resend, no ConfirmSignUp — #275 ruling A).
   * Never a blind SignUp (it would duplicate a mixed-case legacy account on the
   * case-sensitive pool) and never a blind InitiateAuth (Cognito fakes an
   * EMAIL_OTP for unknown addresses at random — #275 G1/G2). Probe unavailable
   * ⇒ startEmailClaim throws ⇒ neutral "couldn't send a code" (fail closed).
   * Resolves 'sent' when a code is on its way, 'password-only' for an existing
   * account no code can be emailed to.
   */
  async function sendCode(): Promise<'sent' | 'password-only'> {
    codeUsername.current = null // a NEW send re-resolves the username
    const claim = await startEmailClaim(email.trim())
    if (claim.kind === 'password-only') return 'password-only'
    if (claim.kind === 'otp') armCode('otp', claim.challenge.session, claim.challenge.destination, claim.username)
    else armCode('claim', claim.session, claim.destination, claim.username)
    return 'sent'
  }

  /**
   * PRIMARY path: email a sign-in code (sendCode — EMAIL_OTP for a verified
   * account, the claim code otherwise), then advance to the CODE step with the
   * SAME copy either way, so whether the address had an account is never
   * revealed. The old red "isn't verified yet" dead-end is gone: an unverified
   * or missing account is CLAIMED by the code instead.
   */
  async function requestCode() {
    if (!emailValid || busy || rateWait > 0) return
    setError(null)
    setNotice(null)
    setBusy(true)
    setStep('SENDING_CODE')
    try {
      const outcome = await sendCode()
      setBusy(false)
      if (outcome === 'password-only') {
        // A confirmed account whose email was never verified: Cognito cannot
        // email it any code, so hand over to the password path (probe-driven,
        // incl. "Forgot your password?").
        clearPendingEmailCode()
        void submitEmail()
        return
      }
      resetTransientAt('CODE')
      setNotice(CODE_SENT_NOTICE)
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      setStep('EMAIL')
      showSendError(err)
    }
  }

  /**
   * CLAIM path: ConfirmSignUp with the entered code (email_verified=true), then
   * sign in WITHOUT a password — "sign in after sign-up" (ConfirmSignUp Session →
   * InitiateAuth USER_AUTH), else ONE EMAIL_OTP fallback. Lands via
   * finishNewAccount so the first sign-in runs the legacy marry-up lookup
   * (GET /me/legacy-profile) exactly like the password create-account branch.
   */
  async function submitClaimCode() {
    setError(null)
    setBusy(true)
    setStep('AUTHING')
    let signInSession: string | undefined
    try {
      const confirmed = await confirmClaimCode(codeUser(), code.trim(), claimSession.current ?? undefined, undefined, EXACT)
      signInSession = confirmed.session
    } catch (e) {
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      if (err.code !== 'NotAuthorized') {
        // NotAuthorized = already CONFIRMED (e.g. a double submit) — fall
        // through to the OTP fallback; every other failure stays on CODE.
        setBusy(false)
        setStep('CODE')
        if (err.code === 'CodeMismatch') setError("That code isn't right — check the newest email.")
        else if (err.code === 'ExpiredCode') setError('That code has expired — tap Resend code for a fresh one.')
        else setError(mapErrorCopy(err))
        return
      }
    }
    clearPendingEmailCode()
    claimSession.current = null
    if (signInSession) {
      try {
        const tokens = await signInAfterClaim(codeUser(), signInSession, undefined, EXACT)
        await finishNewAccount(tokens)
        return
      } catch {
        // Sign-in-after-sign-up unavailable/failed — fall back to one EMAIL_OTP.
      }
    }
    claimConfirmed.current = true
    try {
      const challenge = await requestEmailCode(codeUser(), undefined, EXACT)
      armCode('otp', challenge.session, challenge.destination, codeUser())
      setBusy(false)
      resetTransientAt('CODE')
      setNotice(CLAIM_FINISH_NOTICE)
    } catch (e) {
      setBusy(false)
      resetTransientAt('EMAIL')
      showSendError(e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.'))
    }
  }

  /**
   * PRIMARY path: answer the EMAIL_OTP challenge with the entered code
   * (userAuth.submitEmailCode). Uses the in-memory session, falling back to the
   * persisted one (a deep-link opened in a fresh tab). NO session at all (e.g. a
   * different-device deep-link) ⇒ an honest "request a fresh code" prompt, never
   * an opaque failure. On success the tokens flow into the SAME session layer as
   * the password path (storeSession, via finish()). A claim code goes to
   * submitClaimCode instead.
   */
  async function submitCode() {
    if (code.trim().length < CODE_INPUT_MIN_LENGTH || busy) return
    if (codeMode.current === 'claim') {
      void submitClaimCode()
      return
    }
    const session = emailCodeSession.current ?? matchedPendingSession()
    if (!session) {
      setError('Your code session has expired — tap Resend code for a fresh one.')
      return
    }
    setError(null)
    setBusy(true)
    setStep('AUTHING')
    try {
      const tokens = await submitEmailCode(codeUser(), code.trim(), session, undefined, EXACT)
      clearPendingEmailCode()
      emailCodeSession.current = null
      if (claimConfirmed.current) {
        // Second (fallback) code of a claim — still the account's FIRST login.
        claimConfirmed.current = false
        await finishNewAccount(tokens)
      } else {
        finish(tokens)
      }
    } catch (e) {
      setBusy(false)
      setStep('CODE')
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      if (err.code === 'CodeMismatch') {
        setError("That code isn't right — check the newest email.")
      } else if (err.code === 'ExpiredCode' || err.code === 'NotAuthorized') {
        // The code or its challenge session has lapsed — a fresh send is needed.
        emailCodeSession.current = null
        clearPendingEmailCode()
        setError('That code has expired — tap Resend code for a fresh one.')
      } else {
        setError(mapErrorCopy(err))
      }
    }
  }

  /** Resend on the CODE step. OTP: a fresh InitiateAuth (new code + new
   *  session; the previous code is invalidated), re-entering the claim path if
   *  the address cannot take an OTP. Claim: ResendConfirmationCode (the SignUp
   *  Session is kept for sign-in-after-sign-up). Same copy either way. */
  async function doResendCode() {
    if (resend > 0 || busy || rateWait > 0) return
    setError(null)
    try {
      if (codeMode.current === 'claim') {
        const destination = await resendClaimCode(codeUser(), undefined, EXACT)
        armCode('claim', claimSession.current ?? undefined, destination, codeUser())
      } else if ((await sendCode()) === 'password-only') {
        clearPendingEmailCode()
        void submitEmail()
        return
      }
      setCode('')
      setNotice('We sent a fresh code.')
      setResend(RESEND_COOLDOWN_S)
    } catch (e) {
      showSendError(e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.'))
    }
  }

  /** SECONDARY (legacy/optional): drop to the password path from the current
   *  email — reuses the existing probe-driven state machine (submitEmail). */
  function goPasswordInstead() {
    if (!emailValid || busy) return
    emailCodeSession.current = null
    claimSession.current = null
    codeUsername.current = null
    codeMode.current = 'otp'
    clearPendingEmailCode()
    void submitEmail()
  }

  async function submitPassword() {
    if (!password || busy) return
    setError(null)
    setBusy(true)
    try {
      const result = await signIn(email.trim(), password)
      if (result.status === 'newPasswordRequired') {
        pendingNewPw.current = result.pending
        setBusy(false)
        resetTransientAt('NEW_PASSWORD')
        return
      }
      finish(result.tokens)
    } catch (e) {
      setBusy(false)
      const err = fail(e)
      // We only reach PASSWORD for an account we already KNOW exists (the
      // SET_PASSWORD crossover, or the unconfirmed/reset resume) — so a failure
      // here is a wrong password, never a missing account. Never imply the
      // email is wrong; point the user at the reset option below.
      if (err.code === 'NotAuthorized' || err.code === 'UserNotFound') {
        setError('That password isn’t right — try again, or reset it below.')
      }
    }
  }

  async function submitCreatePassword() {
    if (!password || busy) return
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const res = await signUp(email.trim(), password)
      createdPassword.current = password // held for auto sign-in after confirm
      setBusy(false)
      if (res.userConfirmed) {
        // Already confirmed (rare) — sign straight in.
        await autoSignIn()
      } else {
        resetTransientAt('CONFIRM_CODE')
      }
    } catch (e) {
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      if (err.code === 'UsernameExists') {
        // The account already EXISTS (signUp is our authoritative existence
        // signal — the SRP probe can't tell, PreventUserExistenceErrors). Try
        // the entered password as the EXISTING one (ClaimLanding smart-path):
        // correct → straight in; wrong → the pure sign-in step with a reset
        // option. We never accuse a NEW email of a bad password.
        try {
          const result = await signIn(email.trim(), password)
          if (result.status === 'newPasswordRequired') {
            pendingNewPw.current = result.pending
            setBusy(false)
            resetTransientAt('NEW_PASSWORD')
            return
          }
          finish(result.tokens)
        } catch {
          createdPassword.current = null
          setBusy(false)
          resetTransientAt('PASSWORD')
          setNotice('You already have an account — enter your password.')
        }
        return
      }
      setBusy(false)
      if (err.code === 'InvalidPassword' || err.code === 'InvalidParameter') {
        // Render the SERVER's password-policy message verbatim (spec).
        setError(err.message)
      } else {
        setError(mapErrorCopy(err))
      }
    }
  }

  async function autoSignIn() {
    const pw = createdPassword.current
    if (!pw) {
      // No held password (resume path) — go sign in manually.
      resetTransientAt('PASSWORD')
      setNotice('Email confirmed — sign in.')
      return
    }
    setBusy(true)
    setStep('AUTHING')
    try {
      const result = await signIn(email.trim(), pw)
      if (result.status === 'newPasswordRequired') {
        pendingNewPw.current = result.pending
        setBusy(false)
        resetTransientAt('NEW_PASSWORD')
        return
      }
      // NEW-ACCOUNT branch: check for a legacy match → pre-filled wizard review
      // (CMT-035). Falls back to the home redirect on no match / failure.
      await finishNewAccount(result.tokens)
    } catch (e) {
      setBusy(false)
      resetTransientAt('PASSWORD')
      fail(e)
    }
  }

  async function submitConfirmCode() {
    if (code.trim().length < 6 || busy) return
    setError(null)
    setBusy(true)
    try {
      await confirmSignUp(email.trim(), code.trim())
      setBusy(false)
      if (createdPassword.current) {
        await autoSignIn()
      } else {
        resetTransientAt('PASSWORD')
        setNotice('Email confirmed — sign in.')
      }
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      if (err.code === 'CodeMismatch') {
        setError("That code isn't right — check the newest email.")
      } else if (err.code === 'ExpiredCode') {
        setError('That code has expired — tap Resend code for a fresh one.')
      } else {
        setError(mapErrorCopy(err))
      }
    }
  }

  async function doResend() {
    if (resend > 0 || busy) return
    setError(null)
    try {
      // FORGOT_CODE's code comes from ForgotPassword, not the signup lane —
      // resendConfirmationCode errors for a CONFIRMED user (the reset case).
      // Re-invoke the matching sender per step; UserNotFound stays neutral
      // (same non-enumeration rule as beginForgot).
      if (step === 'FORGOT_CODE') {
        try {
          await forgotPassword(authUsername(), { exactUsername: true })
        } catch (e) {
          const err = e instanceof AuthFlowError ? e : null
          if (!err || err.code !== 'UserNotFound') throw e
        }
      } else {
        await resendCode(email.trim())
      }
      setNotice('We sent a fresh code.')
      setResend(RESEND_COOLDOWN_S)
    } catch (e) {
      fail(e)
    }
  }

  async function submitNewPassword() {
    if (!password || busy || !pendingNewPw.current) return
    setError(null)
    setBusy(true)
    try {
      const tokens = await completeNewPassword(pendingNewPw.current, password)
      finish(tokens)
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      setError(err.code === 'InvalidPassword' ? err.message : mapErrorCopy(err))
    }
  }

  /**
   * Start the reset flow (BOARDROOM-2026-09-01-180926: reachable from BOTH
   * password steps). `returnTo` is the step a hard failure falls back to, so a
   * CREATE_PASSWORD user is never dumped onto a sign-in step they didn't come
   * from. Unknown emails stay NON-ENUMERATING: the consumer pool has
   * PreventUserExistenceErrors enabled, so ForgotPassword on an unknown email
   * returns a simulated success (no UserNotFound) — and if that guard ever
   * slipped, we defensively map UserNotFound to the SAME neutral success
   * outcome rather than surfacing "no account for that email".
   */
  async function beginForgot(returnTo: 'PASSWORD' | 'CREATE_PASSWORD' = 'PASSWORD') {
    setError(null)
    setNotice(null)
    setBusy(true)
    setStep('FORGOT_SEND')
    try {
      // Probe-resolved username (case-correct on the case-sensitive pool) —
      // NOT the raw email: a mis-cased ForgotPassword "succeeds" silently and
      // no code ever arrives (checklist #3).
      await forgotPassword(authUsername(), { exactUsername: true })
      setBusy(false)
      resetTransientAt('FORGOT_CODE')
      setNotice(FORGOT_SENT_NOTICE)
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', e instanceof Error ? e.message : 'Something went wrong.')
      if (err.code === 'UserNotFound') {
        // Neutral outcome — indistinguishable from success (anti-enumeration).
        resetTransientAt('FORGOT_CODE')
        setNotice(FORGOT_SENT_NOTICE)
        return
      }
      resetTransientAt(returnTo)
      fail(err)
    }
  }

  async function submitForgotCode() {
    if (code.trim().length < 6 || !password || busy) return
    setError(null)
    setBusy(true)
    try {
      // Same resolved username the reset code was issued to.
      await confirmForgotPassword(authUsername(), code.trim(), password, { exactUsername: true })
      setBusy(false)
      resetTransientAt('PASSWORD')
      setNotice('Password updated — sign in.')
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      if (err.code === 'CodeMismatch') {
        setError("That code isn't right — check the newest email.")
      } else if (err.code === 'InvalidPassword') {
        setError(err.message)
      } else {
        setError(mapErrorCopy(err))
      }
    }
  }

  function backToEmail() {
    createdPassword.current = null
    pendingNewPw.current = null
    resolvedUsername.current = null // never let a stale resolution target the OLD email's account
    emailCodeSession.current = null
    claimSession.current = null
    codeUsername.current = null
    codeMode.current = 'otp'
    claimConfirmed.current = false
    clearPendingEmailCode()
    setCodeDestination(null)
    resetTransientAt('EMAIL')
  }

  /**
   * Explicit "New to Trumin? Create an account" intent (BOARDROOM-2026-09-01-
   * 180926). Routes straight into the EXISTING create path (the neutral
   * SET_PASSWORD step) with NO probe and NO network call, so clicking it can
   * never reveal whether the email has an account. An existing email that comes
   * through here is still safe: the SET_PASSWORD crossover signs it in (or
   * hands it to the PASSWORD step) exactly as today. The implicit-creation
   * redesign is a separate item — this is intent signalling only.
   */
  function goCreateAccount() {
    if (!emailValid || busy) return
    createdPassword.current = null
    resetTransientAt('CREATE_PASSWORD')
  }

  // --- render helpers ------------------------------------------------------

  const enterSubmits = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      fn()
    }
  }

  const errorBlock = rateWait > 0 ? (
    <p role="alert" className="secondary" data-testid="login-error" style={{ fontSize: 12.5, color: 'var(--error)', margin: 0 }}>
      Too many attempts right now — try again in {rateWait} second{rateWait === 1 ? '' : 's'}.
    </p>
  ) : error ? (
    <p role="alert" className="secondary" data-testid="login-error" style={{ fontSize: 12.5, color: 'var(--error)', margin: 0 }}>
      {error}
    </p>
  ) : null
  const noticeBlock = notice ? (
    <p role="status" className="secondary" data-testid="login-notice" style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
      {notice}
    </p>
  ) : null

  const emailRow = (
    <div className="row" style={{ alignItems: 'center', gap: 8, justifyContent: 'center', fontSize: 12.5 }}>
      <span className="secondary" style={{ overflowWrap: 'anywhere' }}>{email.trim()}</span>
      <button type="button" data-testid="login-change-email" onClick={backToEmail} style={linkBtnStyle}>Change</button>
    </div>
  )

  function frame(children: React.ReactNode) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
        <div className="col" style={{ width: 360, gap: 22 }}>
          <div className="col" style={{ alignItems: 'center', gap: 14 }}>
            <Brand size={34} />
            <div className="col" style={{ alignItems: 'center', gap: 4, textAlign: 'center' }}>
              <h1 style={{ fontSize: 21 }}>My Trumin</h1>
              <p className="secondary" style={{ fontSize: 13, maxWidth: 300 }}>
                Your orders, tickets and race prep — one login for every start line.
              </p>
            </div>
          </div>
          {children}
        </div>
      </div>
    )
  }

  if (!configured) {
    return frame(
      <p className="faint" data-wired="false" role="status" style={{ fontSize: 12, textAlign: 'center', color: 'var(--warn)' }}>
        Sign-in is not configured in this build (no consumer pool values) —
        there is no demo mode; this app only shows real account data.
      </p>,
    )
  }

  // --- per-state UI --------------------------------------------------------

  let body: React.ReactNode

  if (handedOff) {
    return frame(
      <p role="status" data-testid="login-handed-off" className="secondary" style={{ fontSize: 13.5, textAlign: 'center', margin: 0 }}>
        Continue in your other my.trumin tab — you can close this one.
      </p>,
    )
  }

  if (step === 'EMAIL') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-email" onPointerDownCapture={syncEmailFromField} onFocusCapture={syncEmailFromField} onKeyDownCapture={syncEmailFromField}>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-email">Email</label>
          <input
            id="login-email" data-testid="login-email" type="email" autoFocus autoComplete="email"
            inputMode="email" placeholder="you@email.com" value={email} ref={emailInputRef}
            onChange={(e) => setEmail(e.target.value)} onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            onKeyDown={enterSubmits(submitEmail)} style={inputStyle}
          />
          <EmailHiddenCharNotice show={emailHidden} testid="login-email-hidden-char" />
        </div>
        {errorBlock}
        {noticeBlock}
        {/* PRIMARY (WU-04 canon): email a sign-in code — the native USER_AUTH
            EMAIL_OTP flow. cmp.button (PROGRAMME-CANON-MANAGE-TRUMIN-V1.4 §1),
            realised by this repo's <Button> idiom (src/components/ui.tsx). */}
        <Button variant="primary" full onClick={requestCode} disabled={!emailValid || busy || rateWait > 0}>
          Email me a sign-in code
        </Button>
        {/* SECONDARY (legacy/optional): password path, DE-EMPHASISED to a link
            per WU-04 / PROGRAMME-DESIGN-AUTH-POPULATION-MODEL-V1 v3. */}
        <div className="row" style={{ justifyContent: 'center', gap: 6, fontSize: 12.5 }}>
          <button
            type="button" data-testid="login-use-password" onClick={goPasswordInstead}
            disabled={!emailValid || busy}
            style={{ ...linkBtnStyle, opacity: !emailValid || busy ? 0.5 : 1 }}
          >
            Use a password instead
          </button>
        </div>
        {/* Distinct create intent (BOARDROOM-2026-09-01-180926): routes into the
            EXISTING create path with no probe/network — reveals nothing pre-submit. */}
        <div className="row" style={{ justifyContent: 'center', gap: 6, fontSize: 12.5 }}>
          <span className="secondary">New to Trumin?</span>
          <button
            type="button" data-testid="login-create-account" onClick={goCreateAccount}
            disabled={!emailValid || busy}
            style={{ ...linkBtnStyle, opacity: !emailValid || busy ? 0.5 : 1 }}
          >
            Create an account
          </button>
        </div>
        <p className="faint" style={{ fontSize: 11, textAlign: 'center', margin: 0 }}>
          Use the email you bought your tickets with — your orders connect to
          your verified email address.
        </p>
      </div>
    )
  } else if (step === 'SENDING_CODE') {
    body = <p role="status" className="secondary" data-testid="login-sending-code" style={{ fontSize: 13, textAlign: 'center' }}>Emailing your sign-in code…</p>
  } else if (step === 'CODE') {
    body = (
      <div className="col" style={{ gap: 14 }} data-testid="login-step-code">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Check your email</h2>
        <p className="secondary" style={{ fontSize: 12.5, textAlign: 'center', margin: 0 }}>
          Enter the sign-in code we sent to {codeDestination ?? maskEmail(email.trim())}.
        </p>
        {emailRow}
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-emailcode">Sign-in code</label>
          {/* LARGE code box + autocomplete="one-time-code" per the approved
              2026-09-16 branded template (PROGRAMME-DESIGN-AUTH-POPULATION-MODEL
              -V1 v3). The deep-link lands here PRE-FILLED and NEVER auto-submits
              — an explicit "Sign in" click is required (LOCKED canon).
              CODE LENGTH (BOARDROOM-2026-10-02-090708): the box takes 6–8 digits
              for EVERY code — the 8-digit EMAIL_OTP and the 6-digit claim
              (sign-up verification) code — with identical maxlength, no
              digit-count placeholder and the same enable threshold, so its shape
              never reveals which code (i.e. whether an account existed) was sent. */}
          <input
            id="login-emailcode" data-testid="login-emailcode" type="text" autoFocus inputMode="numeric"
            autoComplete="one-time-code" maxLength={CODE_INPUT_MAX_LENGTH} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_INPUT_MAX_LENGTH))} onKeyDown={enterSubmits(submitCode)}
            style={{
              ...inputStyle, minHeight: 64, fontSize: 30, fontWeight: 800, textAlign: 'center',
              letterSpacing: '0.4em', fontFamily: 'var(--font-mono, monospace)',
            }}
          />
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitCode} disabled={code.trim().length < CODE_INPUT_MIN_LENGTH || busy}>
          {busy ? 'Signing you in…' : 'Sign in'}
        </Button>
        <div className="row" style={{ justifyContent: 'center' }}>
          <Button variant="ghost" small onClick={doResendCode} disabled={resend > 0 || busy || rateWait > 0}>
            {resend > 0 ? `Resend code (${resend}s)` : 'Resend code'}
          </Button>
        </div>
        <div className="row" style={{ justifyContent: 'center', gap: 6, fontSize: 12.5 }}>
          <button type="button" data-testid="login-use-password" onClick={goPasswordInstead} disabled={busy} style={linkBtnStyle}>
            Use a password instead
          </button>
        </div>
      </div>
    )
  } else if (step === 'CHECKING') {
    body = <p role="status" className="secondary" data-testid="login-checking" style={{ fontSize: 13, textAlign: 'center' }}>Checking your email…</p>
  } else if (step === 'AUTHING') {
    body = <p role="status" className="secondary" data-testid="login-authing" style={{ fontSize: 13, textAlign: 'center' }}>Signing you in…</p>
  } else if (step === 'FORGOT_SEND') {
    body = <p role="status" className="secondary" data-testid="login-forgot-send" style={{ fontSize: 13, textAlign: 'center' }}>Sending your reset code…</p>
  } else if (step === 'PASSWORD') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-password">
        {emailRow}
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-password">Password</label>
          <input
            id="login-password" data-testid="login-password" type="password" autoFocus
            autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)} onKeyDown={enterSubmits(submitPassword)} style={inputStyle}
          />
          {/* Prominent, industry-standard placement: directly under the field
              (BOARDROOM-2026-09-01-180926), not buried below the button. */}
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 2 }}>
            <button type="button" data-testid="login-forgot" onClick={() => void beginForgot('PASSWORD')} style={linkBtnStyle}>
              Forgot your password?
            </button>
          </div>
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitPassword} disabled={!password || busy}>
          {busy ? 'Signing you in…' : 'Sign in'}
        </Button>
      </div>
    )
  } else if (step === 'CREATE_PASSWORD') {
    // Neutral SET_PASSWORD framing (ClaimLanding pattern): works for a brand-NEW
    // buyer (we create the account) AND a returning one (we sign them in), since
    // the pool's PreventUserExistenceErrors means we can't know which up front.
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-create">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Enter your password</h2>
        {emailRow}
        <div className="col" style={{ gap: 6 }}>
          {/* BOARDROOM-2026-09-01-174401: "Set your password" / "Create new
              password" read to EXISTING customers as "my account is gone" —
              this step serves both cases, so headline + label stay neutral and
              the footnote below carries the two readings. (Supersedes the
              CMT-2026-08-14-038 t2 create-action label.) */}
          <label style={labelStyle} htmlFor="login-new-password">Password</label>
          <input
            id="login-new-password" data-testid="login-create-password" type="password" autoFocus
            autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} onKeyDown={enterSubmits(submitCreatePassword)} style={inputStyle}
          />
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>At least 8 characters.</p>
          {/* Forgot affordance on THIS step (BOARDROOM-2026-09-01-180926) — but it
              emails a SIGN-IN CODE, not a password reset (Duncan 2026-10-08, the
              claim canon CMT-2026-10-02-005). This step is reached when the probe
              did NOT confirm an account (no account / unverified / ambiguous), and
              a reset there dead-ends: PreventUserExistenceErrors fakes success and
              no email ever comes. The code path serves every case with the same
              copy: a verified account gets EMAIL_OTP, anyone else claims by code. */}
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 2 }}>
            <button type="button" data-testid="login-forgot" onClick={() => void requestCode()} style={linkBtnStyle}>
              Forgot your password? Email me a sign-in code
            </button>
          </div>
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitCreatePassword} disabled={!password || busy}>
          {busy ? 'One moment…' : 'Continue'}
        </Button>
        <p className="faint" style={{ fontSize: 11, textAlign: 'center', margin: 0 }}>
          New here? This creates your account. Already have one? Enter your
          existing password to sign in.
        </p>
      </div>
    )
  } else if (step === 'CONFIRM_CODE') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-confirm">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Check your email</h2>
        <p className="secondary" style={{ fontSize: 12.5, textAlign: 'center', margin: 0 }}>
          We sent a 6-digit code to {maskEmail(email.trim())}.
        </p>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-code">Confirmation code</label>
          <input
            id="login-code" data-testid="login-code" type="text" autoFocus inputMode="numeric"
            autoComplete="one-time-code" maxLength={6} placeholder="123456" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={enterSubmits(submitConfirmCode)} style={inputStyle}
          />
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitConfirmCode} disabled={code.trim().length < 6 || busy}>
          {busy ? 'Confirming…' : 'Confirm'}
        </Button>
        <div className="row" style={{ justifyContent: 'center' }}>
          <Button variant="ghost" small onClick={doResend} disabled={resend > 0 || busy}>
            {resend > 0 ? `Resend code (${resend}s)` : 'Resend code'}
          </Button>
        </div>
      </div>
    )
  } else if (step === 'NEW_PASSWORD') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-newpassword">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Set a new password</h2>
        <p className="secondary" style={{ fontSize: 12.5, textAlign: 'center', margin: 0 }}>
          Your account needs a new password to continue.
        </p>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-set-password">New password</label>
          <input
            id="login-set-password" data-testid="login-set-password" type="password" autoFocus
            autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} onKeyDown={enterSubmits(submitNewPassword)} style={inputStyle}
          />
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>At least 8 characters.</p>
        </div>
        {errorBlock}
        <Button variant="primary" full onClick={submitNewPassword} disabled={!password || busy}>
          {busy ? 'Saving…' : 'Save and continue'}
        </Button>
      </div>
    )
  } else if (step === 'FORGOT_CODE') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-forgot">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Reset your password</h2>
        <p className="secondary" style={{ fontSize: 12.5, textAlign: 'center', margin: 0 }}>
          Enter your reset code and choose a new password.
        </p>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-forgot-code">Reset code</label>
          <input
            id="login-forgot-code" data-testid="login-forgot-code" type="text" autoFocus inputMode="numeric"
            autoComplete="one-time-code" maxLength={6} placeholder="123456" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} style={inputStyle}
          />
        </div>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-forgot-newpw">New password</label>
          <input
            id="login-forgot-newpw" data-testid="login-forgot-password" type="password"
            autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} onKeyDown={enterSubmits(submitForgotCode)} style={inputStyle}
          />
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>At least 8 characters.</p>
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitForgotCode} disabled={code.trim().length < 6 || !password || busy}>
          {busy ? 'Updating…' : 'Update password'}
        </Button>
        <div className="row" style={{ justifyContent: 'center' }}>
          <Button variant="ghost" small onClick={doResend} disabled={resend > 0 || busy}>
            {resend > 0 ? `Resend code (${resend}s)` : 'Resend code'}
          </Button>
        </div>
        {/* The no-code exit (BOARDROOM 2026-10-08, prod reset report): a reset on
            an address with NO account "succeeds" silently (PreventUserExistence-
            Errors). The exit is the CLAIM path (CMT-2026-10-02-005): a sign-in
            code signs in a verified account, and creates/claims any other —
            incl. past customers of the old system. Same copy either way. */}
        <div className="row" style={{ justifyContent: 'center', gap: 6, fontSize: 12.5 }}>
          <span className="secondary">No code?</span>
          <button
            type="button" data-testid="login-forgot-sign-in-code" onClick={() => void requestCode()}
            disabled={busy} style={{ ...linkBtnStyle, opacity: busy ? 0.5 : 1 }}
          >
            Email me a sign-in code
          </button>
        </div>
      </div>
    )
  }

  return frame(body)
}

/** Copy for a failure to SEND a code (OTP or claim). Never the password copy
 *  (a SignUp NotAuthorized means "sign-up not permitted", not a bad password)
 *  and never a raw Cognito message — existence-neutral by construction. */
function sendErrorCopy(err: AuthFlowError): string {
  switch (err.code) {
    case 'LimitExceeded':
    case 'TooManyRequests':
    case 'Network':
      return mapErrorCopy(err)
    case 'CodeDeliveryFailure':
      return 'We couldn’t email a code to that address — check it and try again.'
    default:
      return 'We couldn’t send a code just now — try again, or use a password instead.'
  }
}

/** Map a normalised auth error to consumer-facing copy. InvalidPassword is
 *  handled at the call sites (server message rendered verbatim). */
function mapErrorCopy(err: AuthFlowError): string {
  switch (err.code) {
    case 'NotAuthorized':
      // Never "email or password" — by the time we sign in we already know the
      // account exists, so only the password can be wrong.
      return 'That password isn’t right — try again, or reset it below.'
    case 'UserNotFound':
      return "We couldn't find an account for that email."
    case 'CodeMismatch':
      return "That code isn't right — check the newest email."
    case 'ExpiredCode':
      return 'That code has expired — tap Resend code for a fresh one.'
    case 'LimitExceeded':
    case 'TooManyRequests':
      return 'Too many attempts — please wait a moment and try again.'
    case 'Network':
      return 'Network problem — check your connection and try again.'
    case 'InvalidPassword':
    case 'InvalidParameter':
      return err.message
    default:
      return err.message || 'Something went wrong. Please try again.'
  }
}
